import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Admin-only workflow triage endpoints backing the hidden `/workflow-admin`
 * page. Built for incident response: find runaway fanout trees, terminate a
 * WHOLE tree (root + abandoned children — a plain run cancel only stops the
 * parent), and bulk-manage scheduled snooze reruns.
 */

export const WorkflowAdminStatusCountSchema = z.object({
  status: z.string(),
  count: z.number().int(),
});

export const WorkflowAdminOverviewOutputSchema = z.object({
  /** Run counts per status for the caller's organization. */
  statusCounts: z.array(WorkflowAdminStatusCountSchema),
  /** Runs currently consuming worker capacity (running / queued / idle). */
  activeRunCount: z.number().int(),
  /** Future snooze reruns waiting on their start delay. */
  scheduledRerunCount: z.number().int(),
});

export const WorkflowAdminActiveTreeSchema = z.object({
  /** Root WorkflowRun id — the Temporal workflow id prefix for the tree. */
  rootRunId: z.uuid(),
  /** Temporal workflow id of the root execution. */
  temporalWorkflowId: z.string(),
  workflowId: z.uuid(),
  workflowName: z.string(),
  rootStatus: z.string(),
  rootStopReason: z.string().nullable(),
  triggeredAt: z.iso.datetime(),
  /** Active (running/queued/idle) runs in the tree, including the root. */
  activeRunCount: z.number().int(),
  /** Most recent update across the tree's active runs. */
  latestActivityAt: z.iso.datetime().nullable(),
});

export const ListActiveRunTreesInputSchema = z.object({
  limit: z.number().int().min(1).max(200).default(50),
});

export const ListActiveRunTreesOutputSchema = z.object({
  trees: z.array(WorkflowAdminActiveTreeSchema),
  /** Total active runs org-wide (may exceed the runs in the listed trees). */
  totalActiveRuns: z.number().int(),
});

export const TerminateRunTreeInputSchema = z.object({
  /** Any run id in the tree — resolved up to the root before terminating. */
  workflowRunId: z.uuid(),
  reason: z.string().min(3).max(500),
  /**
   * Also cancel scheduled snooze reruns whose root original is in this tree,
   * so the tree doesn't resurrect itself tomorrow. Defaults to true.
   */
  cancelScheduledReruns: z.boolean().default(true),
});

export const TerminateRunTreeOutputSchema = z.object({
  rootRunId: z.uuid(),
  /** Every run row found in the tree (root + all descendants). */
  totalRunsInTree: z.number().int(),
  /** Temporal executions successfully told to terminate. */
  temporalTerminated: z.number().int(),
  /** Temporal executions already gone (closed or never existed). */
  temporalNotFound: z.number().int(),
  /** Temporal terminate calls that errored (transport/auth) — retry these. */
  temporalFailed: z.number().int(),
  /** True when Temporal is disabled in this environment (DB-only cleanup). */
  temporalSkipped: z.boolean(),
  /** DB rows flipped from an active status to `terminated`. */
  dbRunsMarkedTerminated: z.number().int(),
  /** Scheduled snooze reruns cancelled alongside the tree. */
  scheduledRerunsCancelled: z.number().int(),
});

export const WorkflowAdminScheduledRerunSchema = z.object({
  runId: z.uuid(),
  workflowId: z.uuid(),
  workflowName: z.string(),
  scheduledFor: z.iso.datetime().nullable(),
  /** The snooze reason recorded when the rerun was scheduled. */
  reason: z.string().nullable(),
  /** Snooze-chain depth (1 = first automatic rerun of the original). */
  rerunAttempt: z.number().int().nullable(),
  /** The original run this rerun re-executes. */
  rerunOfWorkflowRunId: z.uuid().nullable(),
});

export const ListScheduledRerunsInputSchema = z.object({
  limit: z.number().int().min(1).max(500).default(100),
});

export const ListScheduledRerunsOutputSchema = z.object({
  reruns: z.array(WorkflowAdminScheduledRerunSchema),
  totalScheduled: z.number().int(),
});

export const CancelScheduledRerunsInputSchema = z.object({
  workflowRunIds: z.array(z.uuid()).min(1).max(500),
  reason: z.string().min(3).max(500),
});

export const CancelScheduledRerunsOutputSchema = z.object({
  cancelled: z.number().int(),
  /** Runs that were no longer in `scheduled` status (fired or already gone). */
  alreadyResolved: z.number().int(),
  /**
   * Cancels that errored (e.g. Temporal transport failure after the DB flip).
   * Non-zero means the operator should retry or check Temporal directly.
   */
  failed: z.number().int(),
});

export type WorkflowAdminOverviewOutput = z.infer<
  typeof WorkflowAdminOverviewOutputSchema
>;
export type WorkflowAdminActiveTree = z.infer<
  typeof WorkflowAdminActiveTreeSchema
>;
export type ListActiveRunTreesOutput = z.infer<
  typeof ListActiveRunTreesOutputSchema
>;
export type TerminateRunTreeInput = z.infer<typeof TerminateRunTreeInputSchema>;
export type TerminateRunTreeOutput = z.infer<
  typeof TerminateRunTreeOutputSchema
>;
export type WorkflowAdminScheduledRerun = z.infer<
  typeof WorkflowAdminScheduledRerunSchema
>;
export type ListScheduledRerunsOutput = z.infer<
  typeof ListScheduledRerunsOutputSchema
>;
export type CancelScheduledRerunsOutput = z.infer<
  typeof CancelScheduledRerunsOutputSchema
>;

export const workflowAdminContracts = {
  /** Org-wide run status counts for the triage overview cards. */
  getOverview: oc
    .output(WorkflowAdminOverviewOutputSchema)
    .route({ method: "GET" }),

  /**
   * Active run trees, largest first. Groups every active run under its ROOT
   * run so a 500-child fanout shows as one row with `activeRunCount: 500`
   * instead of 500 anonymous rows.
   */
  listActiveRunTrees: oc
    .input(ListActiveRunTreesInputSchema)
    .output(ListActiveRunTreesOutputSchema)
    .route({ method: "GET" }),

  /**
   * Emergency stop for a whole run tree: terminates the root AND every
   * descendant in Temporal (fanout children are started with
   * ParentClosePolicy.ABANDON, so cancelling only the parent leaves them
   * running), then marks the DB rows terminated.
   */
  terminateRunTree: oc
    .input(TerminateRunTreeInputSchema)
    .output(TerminateRunTreeOutputSchema)
    .route({ method: "POST" }),

  /** Upcoming scheduled snooze reruns, soonest first. */
  listScheduledReruns: oc
    .input(ListScheduledRerunsInputSchema)
    .output(ListScheduledRerunsOutputSchema)
    .route({ method: "GET" }),

  /** Bulk-cancel scheduled reruns before their start delay fires. */
  cancelScheduledReruns: oc
    .input(CancelScheduledRerunsInputSchema)
    .output(CancelScheduledRerunsOutputSchema)
    .route({ method: "POST" }),
} as const;
